/**
 * DemoBanner — thin above-chrome strip rendered when the current tenant
 * has is_demo=true (B-11).
 *
 * Rendered by AppShell via the AboveChromeSlot portal so it sits above
 * the PageHeader band, consistent with SampleDataBanner.
 *
 * QA-v2 D19 (founder-revised): the banner is dismissible PER SESSION. A
 * close (×) control hides it for the current browser session; it reappears
 * on a new session (sessionStorage, NOT localStorage) so the data-reset
 * warning is never permanently lost.
 *
 * Design §5 is_demo gate table: "In-app banner: 'Demo tenant — data
 * does not persist between resets.' No copy mentioning 'nightly' —
 * that was the auto-reset framing we're punting."
 */
import { useState } from "react";
import { AboveChromeSlot } from "@/components/layout/AboveChromeSlot";
import { useMe } from "@/hooks/queries/auth/useMe";

// sessionStorage key for the per-session dismissed flag. Per-session by
// design (sessionStorage clears on new tab-session) so the reset warning
// resurfaces every session.
const DISMISS_KEY = "certain.demoBanner.dismissed";

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    // sessionStorage can throw (privacy mode / SSR) — fail open (show banner).
    return false;
  }
}

export function DemoBanner(): JSX.Element | null {
  const me = useMe();
  const isDemo = me.data?.tenant_is_demo === true;
  const [dismissed, setDismissed] = useState<boolean>(readDismissed);

  if (!isDemo || dismissed) return null;

  function handleDismiss(): void {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Best-effort persist; still hide for the rest of this render session.
    }
    setDismissed(true);
  }

  return (
    <AboveChromeSlot>
      <div
        data-testid="demo-tenant-banner"
        // Contrast fix: this strip renders inside AppShellV2's `data-theme="v2"`
        // subtree, whose surface is LIGHT even while the global <html> is
        // `data-theme="dark"` (the founder-locked default). Tailwind's `dark:`
        // variant keys off ANY `[data-theme="dark"]` ancestor (the <html>), so
        // the dark colors fired on top of the light v2 surface. The old
        // semi-transparent `dark:bg-amber-500/10` then let that light surface
        // bleed through under pale `dark:text-amber-200`, giving ~1.12:1
        // "yellow-on-cream". Fix: OPAQUE amber backgrounds in both variants so
        // the banner is surface-independent and can't reproduce the bleed-through
        // regardless of which `[data-theme]` scope it lands in.
        //   light: amber-950 on amber-100 = 13.45:1 (AA/AAA); icon amber-800 = 6.37:1
        //   dark : amber-100 on amber-950 = 13.45:1 (AA/AAA); icon amber-300 = 10.39:1
        className="flex items-center gap-3 border-b border-amber-300 bg-amber-100 px-content-px py-1.5 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
      >
        <svg
          className="h-3.5 w-3.5 shrink-0 text-amber-800 dark:text-amber-300"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p>
          <span className="font-semibold">Demo tenant</span> — data does not
          persist between resets.
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss demo banner for this session"
          data-testid="demo-tenant-banner-dismiss"
          className="ml-auto -my-1 shrink-0 rounded-sm p-1 text-amber-800 transition-colors hover:bg-amber-200 hover:text-amber-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 dark:text-amber-300 dark:hover:bg-amber-900 dark:hover:text-amber-50"
        >
          <svg
            className="h-3.5 w-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </AboveChromeSlot>
  );
}
